import { useMutation } from '@tanstack/react-query'
import { Button, Col, Form, Input, Modal, message, Row } from 'antd'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { API, COMMONS, MEMBER_UTILS } from '@/utils'
import { useScanErrorHandler } from '@/hooks/useScanErrorHandler'
import { useCheckinSound } from '@/hooks/useCheckinSound'

const DeviceScanModal = (props: {
	isMemberVisit?: any
	isMemberRenewDeviceModalVisible?: any
	hideMemberRenewDeviceModal?: any
	hideMemberRenewCameraModal?: any
	showMemberRenewConfirmModal?: any
}) => {
	const {
		isMemberVisit,
		isMemberRenewDeviceModalVisible,
		hideMemberRenewDeviceModal,
		hideMemberRenewCameraModal,
		showMemberRenewConfirmModal,
	} = props

	const barcodeRef = useRef<any>(null)
	const [memberRenewDeviceForm] = Form.useForm()
	const navigate = useNavigate()
	const { handleScanError } = useScanErrorHandler()
	const { playSuccessSound, playAlreadyCheckedInSound } = useCheckinSound()

	const memberCheckMutation = useMutation({
		mutationFn: (data: any) => API.ADMIN_CHECK_MEMBER(data),
		onSuccess: (response: any) => {
			const data = response?.data

			if (!data) {
				message.warning(COMMONS.WARN_MEMBER_CODE_NOT_EXIST_MSG)
				return
			}

			const { pointsAwarded } = data || {}

			if (pointsAwarded) {
				playSuccessSound() // Per 2026/02/04 spec: sound on successful check-in
				message.success('チェックイン完了')
			} else {
				playAlreadyCheckedInSound()
				message.info('本日の来館は記録済みです')
			}

			showMemberRenewConfirmModal(data || {})
			hideMemberRenewDeviceModal()
		},
		onError: handleScanError,
	})

	useEffect(() => {
		setTimeout(() => {
			if (barcodeRef && barcodeRef.current) {
				barcodeRef.current.focus()
			}
		}, 1000)
	}, [isMemberRenewDeviceModalVisible])

	const memberSearchMutation = useMutation({
		mutationFn: (data: any) => API.ADMIN_GET_MEMBER_BY_CODE(data),
		onSuccess: (response: any) => {
			if (response?.data) {
				showMemberRenewConfirmModal(response.data)
				hideMemberRenewDeviceModal()
			} else {
				message.warning(COMMONS.WARN_MEMBER_CODE_NOT_EXIST_MSG)
			}
		},
		onError: (error: FetchError) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE)
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning(COMMONS.ERROR_SESSION_MSG)
				navigate(COMMONS.ADMIN_LOGIN_ROUTE)
			} else if (error?.response?.status === 404) {
				message.warning(COMMONS.WARN_MEMBER_CODE_NOT_EXIST_MSG)
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error(COMMONS.ERROR_SYSTEM_MSG)
			} else {
				message.error(COMMONS.ERROR_SYSTEM_MSG)
			}
		},
	})

	const handleMemberCheck = (data: any) => {
		const normalizedMemberCode = MEMBER_UTILS.normalizeMemberCode(data.memberCode)
		if (isMemberVisit) {
			const paramData = {
				memberCode: normalizedMemberCode,
				isMemberVisit: isMemberVisit,
			}
			memberCheckMutation.mutate(paramData)
		} else {
			memberSearchMutation.mutate({ memberCode: normalizedMemberCode })
		}
	}
	return (
		<>
			<Modal
				title={`${isMemberVisit ? '来店記録' : '会員更新'}・装置と手入力`}
				open={isMemberRenewDeviceModalVisible}
				onCancel={hideMemberRenewDeviceModal}
				footer={null}
				destroyOnClose
				centered
				styles={{
					body: {
						maxHeight: '80vh',
						overflowY: 'auto',
						overflowX: 'hidden',
					},
				}}
			>
				<Form
					name='memberRenewDeviceForm'
					form={memberRenewDeviceForm}
					onFinish={handleMemberCheck}
					layout='vertical'
					preserve={false}
					size='large'
				>
					<Row justify='center'>
						<Col>
							<Form.Item
								name='memberCode'
								label='会員コード（バーコード）'
								rules={[
									{
										required: true,
										whitespace: true,
										message: '必須です',
									},
								]}
							>
								<Input
									ref={barcodeRef}
									placeholder='例：000000000000000'
									allowClear
									pattern='[0-9]*'
									inputMode='numeric'
								/>
							</Form.Item>
						</Col>
						<Col span={24} className='text-center'>
							<Button
								type='primary'
								htmlType='submit'
								className='px-12'
								loading={memberCheckMutation.isPending}
							>
								確認
							</Button>
						</Col>
					</Row>
				</Form>
			</Modal>
		</>
	)
}

export default DeviceScanModal
